import type {
  LoginResult,
  Me,
  SessionInfo,
  SignupInput,
} from "@l2a/shared";
import type { z } from "zod";
import type {
  acceptInvitationInputSchema,
  changePasswordInputSchema,
  invitationPreviewSchema,
  loginInputSchema,
  resetPasswordInputSchema,
  twoFactorDisableInputSchema,
  twoFactorSetupSchema,
  updateProfileInputSchema,
} from "@l2a/shared";
import { api, ApiError } from "@/lib/api";

/**
 * Auth endpoints — /api/v1/auth/* (contract: packages/shared/src/auth.ts).
 * Paths live ONLY here so they can be aligned with the API in one place.
 */
export type LoginInput = z.input<typeof loginInputSchema>;
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;
export type TwoFactorSetup = z.infer<typeof twoFactorSetupSchema>;
export type UpdateProfileInput = z.input<typeof updateProfileInputSchema>;

export const AUTH_PATHS = {
  me: "/auth/me",
  login: "/auth/login",
  signup: "/auth/signup",
  logout: "/auth/logout",
  switchOrg: "/auth/switch-org",
  forgotPassword: "/auth/forgot-password",
  resetPassword: "/auth/reset-password",
  verifyEmail: "/auth/verify-email",
  resendVerification: "/auth/verify-email/resend",
  changePassword: "/auth/change-password",
  avatar: "/auth/me/avatar",
  sessions: "/auth/sessions",
  revokeOtherSessions: "/auth/sessions/revoke-others",
  twoFactorSetup: "/auth/2fa/setup",
  twoFactorEnable: "/auth/2fa/enable",
  twoFactorDisable: "/auth/2fa/disable",
  invitation: "/auth/invitation",
  acceptInvitation: "/auth/invitation/accept",
} as const;

/** Some endpoints answer with Me directly, others with `{ me }` / `{ status, me }`. */
function pickMe(res: unknown): Me | null {
  if (!res || typeof res !== "object") return null;
  if ("me" in res && (res as { me?: Me }).me) return (res as { me: Me }).me;
  if ("user" in res && "org" in res) return res as Me;
  return null;
}

export const authApi = {
  /** Session probe. Resolves null when signed out (never redirects). */
  async me(signal?: AbortSignal): Promise<Me | null> {
    try {
      return await api.get<Me>(AUTH_PATHS.me, { signal, skipAuthRedirect: true });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.code === "UNAUTHENTICATED")) return null;
      throw err;
    }
  },
  login: (input: LoginInput) => api.post<LoginResult>(AUTH_PATHS.login, input, { skipAuthRedirect: true }),
  async signup(input: SignupInput): Promise<Me | null> {
    return pickMe(await api.post<unknown>(AUTH_PATHS.signup, input, { skipAuthRedirect: true, idempotencyKey: true }));
  },
  logout: () => api.post<void>(AUTH_PATHS.logout, undefined, { skipAuthRedirect: true }),
  async switchOrg(orgId: string): Promise<Me | null> {
    return pickMe(await api.post<unknown>(AUTH_PATHS.switchOrg, { orgId }));
  },
  forgotPassword: (email: string, captchaToken?: string) =>
    api.post<void>(AUTH_PATHS.forgotPassword, { email, ...(captchaToken ? { captchaToken } : {}) }, { skipAuthRedirect: true }),
  resetPassword: (input: z.input<typeof resetPasswordInputSchema>) => api.post<void>(AUTH_PATHS.resetPassword, input, { skipAuthRedirect: true }),
  verifyEmail: (token: string) => api.post<void>(AUTH_PATHS.verifyEmail, { token }, { skipAuthRedirect: true }),
  resendVerification: () => api.post<void>(AUTH_PATHS.resendVerification, {}),
  changePassword: (input: z.input<typeof changePasswordInputSchema>) => api.post<void>(AUTH_PATHS.changePassword, input),
  async updateProfile(input: UpdateProfileInput): Promise<Me | null> {
    return pickMe(await api.patch<unknown>(AUTH_PATHS.me, input));
  },
  uploadAvatar: (file: File, onProgress?: (f: number) => void) =>
    api.upload<{ avatarUrl: string }>(AUTH_PATHS.avatar, file, { onProgress: (f) => onProgress?.(f) }),
  sessions: async (signal?: AbortSignal) => {
    const res = await api.get<{ items: SessionInfo[] } | SessionInfo[]>(AUTH_PATHS.sessions, { signal });
    return Array.isArray(res) ? res : res.items;
  },
  revokeSession: (id: string) => api.delete(`${AUTH_PATHS.sessions}/${id}`),
  revokeOtherSessions: () => api.post<void>(AUTH_PATHS.revokeOtherSessions, {}),
  twoFactorSetup: () => api.post<TwoFactorSetup>(AUTH_PATHS.twoFactorSetup, {}),
  twoFactorEnable: (code: string) => api.post<{ recoveryCodes: string[] }>(AUTH_PATHS.twoFactorEnable, { code }),
  twoFactorDisable: (input: z.input<typeof twoFactorDisableInputSchema>) => api.post<void>(AUTH_PATHS.twoFactorDisable, input),
  invitationPreview: (token: string, signal?: AbortSignal) =>
    api.get<InvitationPreview>(AUTH_PATHS.invitation, { query: { token }, signal, skipAuthRedirect: true }),
  async acceptInvitation(input: z.input<typeof acceptInvitationInputSchema>): Promise<Me | null> {
    return pickMe(await api.post<unknown>(AUTH_PATHS.acceptInvitation, input, { skipAuthRedirect: true }));
  },
};
